// Adoption.tsx — adoption and engagement, from data the product already writes.
//
// This replaces the NotBuilt placeholder. That placeholder was honest and it was
// right about one thing and wrong about the rest: the activation funnel really
// does need product events nobody emits, but MAU/WAU, feature adoption and
// per-customer trend are all derivable from rows that already exist —
// users.last_login_at (migration 054), contract_requests.created_at,
// executed_contracts, extraction_jobs and ai_extracted_fields.
//
// WHAT IS DELIBERATELY NOT HERE. The activation funnel. Nothing writes
// feature-used, first-contract-created or invite-accepted, so a funnel drawn
// here would be a shape with no source. The screen says so rather than
// substituting a weaker proxy and letting it read as the real thing.
//
// EVERY LABEL SAYS WHAT THE NUMBER IS. "Signed in · 7d" is people who signed in,
// not "active users" — the product has no activity signal finer than a login, and
// calling a login count "active users" would claim a measurement nobody took.

import React, { useCallback, useEffect, useState } from "react";
import { PT, fmtNum } from "../../platformTheme";
import { pget, perr } from "./api";
import { PCard, PSection, PBtn, PBanner, PEmpty, PSpinner, PTable, PRow, PStat, PGrid, th, td, tdNum } from "./ui";
import { Sparkline } from "./charts";

interface TenantAdoption {
  tenant_id: string | null;
  tenant_name: string;
  tenant_status?: string;
  users: {
    total: number; active_accounts: number;
    signed_in_7d: number; signed_in_30d: number; never_signed_in: number;
  };
  stickiness_pct: number | null;
  features: {
    requests_raised: number; contracts_recorded: number;
    documents_extracted: number; ai_extraction_used: number;
  };
  series: { date: string; requests: number }[];
}

export const Adoption: React.FC<{ days: number }> = ({ days }) => {
  const [rows, setRows] = useState<TenantAdoption[] | null>(null);
  const [failed, setFailed] = useState<{ tenant_name: string }[]>([]);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setErr(""); setBusy(true);
    const r = await pget("/metrics/adoption", { days });
    setBusy(false); setAt(new Date());
    if (!r.ok) { setErr(perr(r, "Could not load adoption")); setRows([]); return; }
    setRows(r.data?.tenants || []);
    setFailed(r.data?.tenants_failed || []);
    setNote(r.data?.funnel_note || "");
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const totals = (rows || []).reduce((a, t) => ({
    users: a.users + (t.users?.total || 0),
    signed7: a.signed7 + (t.users?.signed_in_7d || 0),
    signed30: a.signed30 + (t.users?.signed_in_30d || 0),
    never: a.never + (t.users?.never_signed_in || 0),
    requests: a.requests + (t.features?.requests_raised || 0),
  }), { users: 0, signed7: 0, signed30: 0, never: 0, requests: 0 });

  return (
    <>
      <PBanner>{err}</PBanner>
      {failed.length > 0 && (
        <PBanner>
          {failed.length} customer{failed.length > 1 ? "s" : ""} could not be read
          ({failed.map((f) => f.tenant_name).join(", ")}) — the totals below are partial.
        </PBanner>
      )}

      <PSection
        title="Adoption"
        sub={`every customer · ${days}-day window${at ? ` · updated ${at.toLocaleTimeString()}` : ""}`}
        right={<PBtn small disabled={busy} onClick={load}>{busy ? "Refreshing…" : "Refresh"}</PBtn>}
      >
        <PGrid min={175}>
          <PStat label="Users" value={rows === null ? "…" : fmtNum(totals.users)} sub="accounts across the fleet" />
          <PStat
            label="Signed in · 7d" value={rows === null ? "…" : fmtNum(totals.signed7)}
            sub={totals.users ? `${Math.round((totals.signed7 / totals.users) * 100)}% of accounts` : "no accounts"}
          />
          <PStat
            label="Signed in · 30d" value={rows === null ? "…" : fmtNum(totals.signed30)}
            sub={totals.users ? `${Math.round((totals.signed30 / totals.users) * 100)}% of accounts` : "no accounts"}
          />
          <PStat
            label="Stickiness"
            // 7d/30d, and only when the denominator is real: 0/0 rendered as 0%
            // reads as "nobody uses it", which is a different claim entirely.
            value={totals.signed30 ? `${Math.round((totals.signed7 / totals.signed30) * 100)}%` : "—"}
            sub={totals.signed30 ? "7-day of 30-day" : "nobody signed in during the window"}
          />
          <PStat
            label="Never signed in" value={rows === null ? "…" : fmtNum(totals.never)}
            sub="invited, never used"
          />
        </PGrid>

        <div style={{ marginTop: 14 }}>
          <PCard style={{ padding: 0 }}>
            {rows === null ? <PSpinner /> : rows.length === 0 ? (
              <PEmpty>No customers to report on.</PEmpty>
            ) : (
              <PTable
                minWidth={980}
                head={<>
                  <th style={th}>Customer</th>
                  <th style={{ ...th, textAlign: "right" }}>Users</th>
                  <th style={{ ...th, textAlign: "right" }}>Signed in 7d</th>
                  <th style={{ ...th, textAlign: "right" }}>Signed in 30d</th>
                  <th style={{ ...th, textAlign: "right" }}>Never</th>
                  <th style={{ ...th, textAlign: "right" }}>Requests</th>
                  <th style={{ ...th, textAlign: "right" }}>Contracts</th>
                  <th style={{ ...th, textAlign: "right" }}>Extractions</th>
                  <th style={th}>Requests per day</th>
                </>}
              >
                {rows.map((t) => {
                  const pts = (t.series || []).map((p) => p.requests);
                  const dormant = t.users?.signed_in_30d === 0 && (t.users?.total || 0) > 0;
                  return (
                    <PRow key={t.tenant_id || t.tenant_name}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{t.tenant_name}</div>
                        {dormant && (
                          <div style={{ fontSize: 11, color: PT.warnTx }}>
                            nobody signed in during the window
                          </div>
                        )}
                      </td>
                      <td style={tdNum}>{fmtNum(t.users?.total)}</td>
                      <td style={tdNum}>{fmtNum(t.users?.signed_in_7d)}</td>
                      <td style={tdNum}>{fmtNum(t.users?.signed_in_30d)}</td>
                      <td style={{ ...tdNum, color: (t.users?.never_signed_in || 0) > 0 ? PT.warnTx : PT.txD }}>
                        {fmtNum(t.users?.never_signed_in)}
                      </td>
                      <td style={tdNum}>{fmtNum(t.features?.requests_raised)}</td>
                      <td style={tdNum}>{fmtNum(t.features?.contracts_recorded)}</td>
                      <td style={tdNum}>{fmtNum(t.features?.documents_extracted)}</td>
                      <td style={td}>
                        {/* A real per-day series from created_at, not a curve
                            inferred from a point-in-time total. */}
                        {pts.length > 1 && pts.some((p) => p > 0)
                          ? <Sparkline points={pts} color={PT.chBlue} w={130} h={22} fill />
                          : <span style={{ fontSize: 11, color: PT.txD }}>no activity</span>}
                      </td>
                    </PRow>
                  );
                })}
              </PTable>
            )}
          </PCard>
        </div>
      </PSection>

      <PSection title="Feature adoption" sub="counted from the tables each feature writes to">
        <PGrid min={230}>
          {(rows || []).map((t) => (
            <PCard key={(t.tenant_id || t.tenant_name) + "-f"}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 9 }}>{t.tenant_name}</div>
              {([
                ["Requests raised", t.features?.requests_raised],
                ["Contracts recorded", t.features?.contracts_recorded],
                ["Documents extracted", t.features?.documents_extracted],
                ["AI extraction used", t.features?.ai_extraction_used],
              ] as [string, number][]).map(([label, v]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "3px 0" }}>
                  <span style={{ color: PT.txM }}>{label}</span>
                  <span style={{ fontWeight: 600, color: v > 0 ? PT.tx : PT.txD }}>{fmtNum(v)}</span>
                </div>
              ))}
            </PCard>
          ))}
        </PGrid>
      </PSection>

      {note && (
        <div style={{ fontSize: 11.5, color: PT.txD, maxWidth: 820, lineHeight: 1.55 }}>{note}</div>
      )}
    </>
  );
};
